import useAccountInfo from "@/hooks/useAccountInfo";
import {
  TOP_LAYER_ATTRIBUTE,
  attachFeedbackButton,
  identifyAdmin,
  startAdminReporting,
} from "@/utils/admin-sentry";
import { adminReporter, isLimeAdminSession } from "@/utils/admin-session";
import { Bug } from "lucide-react";
import { useEffect, useRef, useState } from "react";

/**
 * Sentry's bug-report button and replay buffer, loaded only for a session a
 * Lime admin is driving — including one where they are impersonating a salon,
 * which is where bugs get reproduced.
 */
export function AdminFeedback() {
  const accountInfo = useAccountInfo((state) => state.accountInfo);
  const isAdmin = isLimeAdminSession(accountInfo);
  const reporter = isAdmin ? adminReporter(accountInfo) : null;
  const email = reporter?.email;
  const name = reporter?.name;
  const impersonating = isAdmin && Boolean(accountInfo.superAdmin);
  const [ready, setReady] = useState(false);

  // Identity is read once; the two paths that change it — `logout` and
  // `handleLoginAsAdmin` — both reload the page.
  useEffect(() => {
    // E2E and capture runs log in as the seeded admin; the button and Sentry's
    // form host would sit over the elements they click.
    if (!isAdmin || import.meta.env.VITE_IS_E2E_TEST === "true") return;
    identifyAdmin({ email, name }, impersonating);
    let current = true;
    void startAdminReporting({ impersonating }).then((loaded) => {
      if (current) setReady(loaded);
    });
    return () => {
      current = false;
    };
  }, [isAdmin, email, name, impersonating]);

  return ready ? <ReportBugButton /> : null;
}

function ReportBugButton() {
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!ref.current) return;
    return attachFeedbackButton(ref.current);
  }, []);

  return (
    <button
      ref={ref}
      type="button"
      aria-label="Report a bug"
      title="Report a bug"
      {...{ [TOP_LAYER_ATTRIBUTE]: "true" }}
      // Above HeroUI overlays (z-50), so it can be clicked while a modal is open.
      className="fixed top-1/2 right-0 z-[9999] -translate-y-1/2 rounded-l-md border border-default-200 border-r-0 bg-content1 p-1 text-default-400 opacity-60 shadow-sm transition-opacity hover:text-foreground hover:opacity-100"
    >
      <Bug className="size-3" aria-hidden />
    </button>
  );
}
